import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Reveal from './Reveal';
import OptimizedImage from './OptimizedImage';

const ProfileDetail: React.FC = () => {
  const navigate = useNavigate();
  const timeline = [
    {
      year: '1973年',
      title: '東京、世田谷生まれ',
      description:
        '3歳のときに世田谷区三軒茶屋から町田市玉川学園に引越し。玉川中央幼稚園、町田第5小学校、その後、本町田への転居に伴い、町田第3小学校に転校。桐蔭学園中学・高校卒業、東京女子大学文理学部社会学科卒業。中学3年生のときに父を肺がんで亡くし、以降母ひとり子ひとりのシングル家庭で育つ。',
      image: '/1973.jpg'
    },
    {
      year: '1996年',
      title: '編集者・ライターになる',
      description:
        '大学卒業後、出版社の（株）扶桑社に入社。書店営業部を経てESSE編集部に異動。その後、結婚・出産を機にフリーランスライターとして独立し、生活情報誌と生活実用書中心の執筆活動を行う。インタビュー記事の執筆も精力的にこなす。',
      image: '/1996.jpg'
    },
    {
      year: '2015年',
      title: '「玉川つばめ通信」創刊',
      description:
        '息子ふたりの小学校のPTA会長になったことがきっかけで地域とのつながりが生まれ、玉川学園のひとりメディア「玉川つばめ通信」を立ち上げる。さらに「スナックつばめ」「小料理つばめ」「ゆるつばめ会議」もスタート。これまで玉川学園、町田を中心に1000人以上の方を取材した。2025年9月、創刊10周年の節目に町田パリオでの「パリコレッ！芸術祭3week」に参加。1〜36号＋臨時増刊号等、バックナンバーを一挙展示した。',
      image: '/2015.jpg'
    },
    {
      year: '2018年',
      title: '「玉川音頭」を復活',
      description:
        '玉川学園の駅前広場で1946年にたった1度行われた「玉川音頭」を玉川学園ポケットパークで72年ぶりに復活させる。以降、玉川音頭はまちのあちこちで踊り継がれるようになった。',
      image: '/2018.jpg'
    },
    {
      year: '2021年',
      title: '町田市の再開発問題に関心を持つ',
      description:
        '子ども時代の大切な居場所だった「町田市立博物館」の閉館がきっかけで、町田市の再開発計画に関心・疑問を持つ。同年10月発足の「芹ヶ谷公園をより良くする市民の会」共同代表になる。',
      image: '/2021.jpg'
    },
    {
      year: '2025年',
      title: '政治活動スタート',
      description:
        '子どもの不登校、夫の難病（重症筋無力症）、自身の乳がんを経験し、人生の様々な局面でたくさんの人に支えられたことを実感する。乳がん治療が終わり、子育ても一段落した今、支えてもらった恩返しとして地域に貢献したいと市政へのチャレンジを決意する。',
      image: '/2025.jpg'
    }
  ];

  const profileInfo = {
    name: 'うのつ のぶこ',
    nameKana: 'うのつ のぶこ',
    birth: '昭和48年（1973年）生まれ',
    party: '無所属',
    experience: '編集・ライター歴27年',
    activities: [
      '「玉川つばめ通信」主宰',
      '地域ボランティア活動',
      '「きんじょの本棚」店主',
      '玉ちゃんウクレレ部',
      '町田ゆるぼん部'
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white" style={{ marginTop: '80px' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-pink-500 hover:text-pink-600 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold" style={{ color: '#e3628d' }}>
              プロフィール詳細
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Reveal duration={1.6} delay={0.2}>
              <div className="max-w-4xl mx-auto mb-8">
                <OptimizedImage
                  src="/nobuko_profile.jpg"
                  alt="うのつのぶこ"
                  className="w-4/5 h-auto mx-auto"
                  includeAlternativeFormats={false}
                  widths={[640, 768, 1024, 1280, 1536]}
                  sizes="(max-width: 1024px) 100vw, 1024px"
                  style={{
                    maxHeight: '600px',
                    objectFit: 'contain',
                    WebkitMaskImage: 'radial-gradient(ellipse at center, black 80%, transparent 96%)',
                    maskImage: 'radial-gradient(ellipse at center, black 80%, transparent 96%)'
                  }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>



      {/* Profile Section */}
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Reveal duration={1.2} delay={0.6}>
              <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#e3628d' }}>
                プロフィール
              </h2>
            </Reveal>
            <Reveal duration={1.2} delay={0.9}>
              <div className="bg-white rounded-lg shadow-md p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-800">
                    <strong>{profileInfo.name}</strong>
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      {profileInfo.birth}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      {profileInfo.party}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      {profileInfo.experience}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      芹ヶ谷公園をより良くする市民の会共同代表
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      日本政策学校25期生
                    </li>
                  </ul>
                </div>

                {/* Activities */}
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-800">主な活動</h3>
                  <ul className="space-y-2 text-gray-600">
                    {profileInfo.activities.map((activity, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-2 h-2 rounded-full bg-pink-300 mt-2 mr-3 flex-shrink-0"></span>
                      {activity}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          </Reveal>
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Reveal duration={1.0} delay={0.2}>
            <div className="card">
              <h3 className="text-xl font-bold mb-8 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
                経歴
              </h3>
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <Reveal key={item.year} delay={0.1 * index}>
                    <div className={index > 0 ? 'border-t border-gray-200 mt-6 pt-6' : ''}>
                      <div className="flex items-center space-x-4 md:space-x-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#e3628d' }}>
                            {item.year}
                          </div>
                        </div>
                        <h4 className="text-xl font-bold text-gray-800">
                          {item.title}
                        </h4>
                      </div>
                      <p className="mt-2 text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                      {item.image && (
                        <div className="mt-4 w-full flex justify-center">
                          <OptimizedImage
                            src={item.image}
                            alt={`${item.year} ${item.title}`}
                            className="w-[640px] max-w-full h-auto rounded-md shadow-sm mx-auto"
                            widths={[480, 640, 768, 1024]}
                            sizes="(max-width: 700px) 100vw, 640px"
                          />
                        </div>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Philosophy Section */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Reveal duration={1.2} delay={0.2}>
              <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#e3628d' }}>
                こころざし
              </h2>
            </Reveal>
            <Reveal duration={1.0} delay={0.45}>
            <div className="card">
              <h3 className="text-xl font-bold mb-6 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
                私の想い
              </h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  <span style={{ color: '#e3628d', fontWeight: 'bold' }}>「無所属。右でも左でもなく真ん中」。</span>この言葉には政党の理念に左右されず、 忖度しないで市民一人ひとりの声に耳を傾け、心の通った解決策を見つけたい、という想いを込めています。
                </p>
                <p>
                  子育てや乳がんを経験し、人生の様々な局面で地域の支え、人とのつながりを感じてきました。 今こそ、その恩返しとして地域に貢献したいと、新たな挑戦を決意しました。
                </p>
                <p>
                  町田をより強く、やさしく、しなやかなまちにするために、対話の力を信じて粘り強く活動を続けます。
                </p>
                <div className="mt-2 w-full flex justify-center">
                  <OptimizedImage
                    src="/eki_unotsu.jpg"
                    alt="駅前での活動の様子"
                    className="w-[640px] max-w-full h-auto rounded-md shadow-sm mx-auto"
                    widths={[480, 640, 768, 1024]}
                    sizes="(max-width: 700px) 100vw, 640px"
                  />
                </div>
              </div>
            </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Four Reasons Section */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Reveal duration={1.0} delay={0.2}>
            <div className="card">
              <h3 className="text-xl font-bold mb-6 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
                私が市政にチャレンジする４つの理由
              </h3>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h4 className="font-bold mb-2">１ 2024年春、乳がんになって死を意識し、今後は仕事として地域活動に取り組みたいと思ったから。</h4>
                  <p>
                    乳がんと診断されたのは2024年の3月。その後、部分切除手術と放射線治療を行い、8月に標準治療を終えました。現在の体調は万全で、元通り元気いっぱいに過ごしておりますが、診断時は「なんで私が乳がんに？」と驚くばかりでした。「乳がんは特別な病気ではなく女性の9人に1人がなる時代」とよく報道されますが、他人事として聞くのと、自分がなるのでは大違い。クリニックで「乳がん」という用紙を手渡されたときのショックは今も忘れません。元気が取り柄だと思っていた私でもがんになること、命には限りがあることを実感するとともに、挑戦したいことがあったら躊躇せずやろう、精一杯やりたいことをやろうと思いました。
                  </p>
                  <p className="mt-3">
                    乳がんを経験し、入院や連日の放射線治療で辛いとき、家族や友人だけでなく、SNSでつながる地域の方々が励ましてくれました。人のありがたみを感じるとともに、人はひとりで生きているわけじゃない、社会は助けたり助けられたりの共助で成り立っていることを身にしみて感じました。すっかり回復した今、<span style={{ color: '#e3628d', fontWeight: 'bold' }}>今後は私の経験やネットワークを地域のために使いたい、これからの人生はさまざまな地域課題の解決に取り組みたいと思っています。</span>
                  </p>
                </div>
                <div className="border-t border-gray-200 mt-6 pt-6">
                  <h4 className="font-bold mb-2">２ 私の住むまちが、ずっと魅力的なまちであるために。</h4>
                  <p>
                    私は3歳から町田に住み、2015年から「玉川つばめ通信」という地域発信型のフリーペーパーを発行してきました。私の住むまちをもっと楽しくしたくて、フリペの発行だけでなく、文房具屋さんの軒先を借りて「スナックつばめ」という不定期スナックを開催したり、「玉川音頭」というまちの盆踊りを復活させたり(つばめ演芸会)しました。普段は全くつながりがない人同士でも、ふとしたきっかけでかかわり、イベントを通して少し距離が縮まる偶然が私は好きです。
                  </p>
                  <p className="mt-3">
                    地域の人のご縁をつないで、巻き込んで、無理のない活動量で続けていくことが、地域活性化の鍵だと私は思っています。遠回りのように見えても手間暇かけてイベントを繰り返すことが、関係性構築のための近道だと思っています。都会でもあり、田舎でもあるトカイナカ町田の個性を活かしつつ、地域活性化を進めていきたいです。<span style={{ color: '#e3628d', fontWeight: 'bold' }}>つくりたいのは活気があって、住み心地のよい町田です。</span>
                  </p>
                </div>
                <div className="border-t border-gray-200 mt-6 pt-6">
                  <h4 className="font-bold mb-2">３ 自身の編集とライター経験を、市政に活かしたいと思ったから</h4>
                  <p>
                    私は大学卒業後、出版社に入社して編集者になり、独立後はフリーランスで編集とライター業を続けてまいりました。編集の仕事は「価値を作る」仕事です。価値を発見し、価値をブラッシュアップし、形にするのが編集の仕事です。そしてライターは、 作った価値を届けたい人にわかりやすく届ける仕事です。<span style={{ color: '#e3628d', fontWeight: 'bold' }}>私の編集とライター経験を活かし、町田市と市民の橋渡し役となり、もっと身近な町田をつくります。</span>
                  </p>
                </div>
                <div className="border-t border-gray-200 mt-6 pt-6">
                  <h4 className="font-bold mb-2">４ 市民参画を促進し、市民に開かれた町田にしたいから</h4>
                  <p>
                    私は約3年前から「芹ヶ谷公園」のプロセスの不透明性に疑問を感じ、「芹ヶ谷公園をよりよくする市民の会」に携わってきました。町田市は「説明会を開いて市民の合意を得た」と言いますが、説明会で異を唱えても、計画が変わることはなく、計画通りに進むだけでなく、当初の約28億円の予算から現在（2025年9月）約48億円まで予算がふくらんでいます。
                  </p>
                  <p className="mt-3">
                    意見が対立する場合こそ対話をし、面倒でも対話を重ねて民主的に進めていくことが大切です。市民が主体的に関われる町田、主体的に関わった甲斐があったと思える町田にしなければと私は考えます。重要な計画について、<span style={{ color: '#e3628d', fontWeight: 'bold' }}>特定の人だけで決めるのではなく、計画段階から市民が関われる町田を目指します。</span>
                  </p>
                </div>
              </div>
            </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Personality and Interests Section */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Reveal duration={1.0} delay={0.2}>
            <div className="card">
              <h3 className="text-xl font-bold mb-6 text-gray-800 border-b border-pink-200 pb-2" style={{ borderBottomColor: '#e3628d' }}>
                私、うのつのぶこはこんな人です
              </h3>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h4 className="font-bold mb-2">こんな性格</h4>
                  <div className="text-gray-700 whitespace-pre-line">
前向き。明るい。行動が早い。まずやってみる。気配りが得意。根拠のない自信がある（これは両親と桐蔭の先生のおかげです）。出し惜しみしない。失敗しても２日後には立ち上がる。人に親切にしたいといつも思っている。決めたことはやり遂げる。人が好き。せっかち。忘れ物が多い。人に気を遣いすぎて疲れる。ときどきおせっかいおばさんでつい世話を焼いてしまう。
                  </div>
                </div>
                <div className="border-t border-gray-200 mt-6 pt-6">
                  <h4 className="font-bold mb-2">好きなこと、もの、場所</h4>
                  <div className="text-gray-700 whitespace-pre-line">
走ること。歌うこと。踊ること。着物を着ること。読書。思いつきでエッセイを書くこと。話を聞くこと。話を聞いてもらうこと。山登り後の温泉後のビール。草戸山のてっぺんで日清カップヌードルシーフード。原稿を書いたあとの本搾りグレープフルーツサワー。焼き鳥ならレバー。フライならアジフライ。おやつなら鳩サブレかピコラ。たけのこの里よりきのこの山派。好きな作家は江國香織と伊藤比呂美と川上弘美（この方々に会いたくて編集者になりました）。町田パリオ3階の丸窓からの眺め。本町田遺跡公園。箱根の天山温泉郷の一休。
                  </div>
                </div>
              </div>
            </div>
            </Reveal>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ProfileDetail; 